/**
 * ITEMS-01 — the parts/materials pricebook. Pegasus-native `item` catalog,
 * modeled on ESC's item master, seeded from the ESC items mirror. Searchable,
 * category-filtered, single + bulk price editing. Distinct from fee_item
 * (service/diagnostic fees). ESC is never written (DEC-22/25).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { searchPricebook } from "./pricebookSearch";
import { pagedAll } from "@/lib/pagedAll";

export interface Item {
  id: string;
  part_number: string;
  description: string | null;
  part_type: string;
  category: string | null;
  subcategory: string | null;
  inactive: boolean;
  units: string | null;
  last_purchase: number | null;
  average_cost: number | null;
  base_price: number | null;
  price_a: number | null;
  price_b: number | null;
  price_c: number | null;
  manufacturer: string | null;
  model: string | null;
  warranty_months: number | null;
  qty_in_stock: number | null;
  vendor: string | null;
  vendor_part_number: string | null;
  source: string;
  image_url: string | null;
  website_url: string | null;
  notes: string | null;
  taxable: boolean;
  archived: boolean;
  markup_pct: number | null;
  esc_item_id: string | null;
  // Equipment fields (GBB quote builder): a first-class model in a system quote.
  sell_price: number | null;
  tier: string | null;
  efficiency: string | null;
  equip_type: string | null;
  /** GBB-ORDER-01: rank inside the tier — the proposal app's "Package
   *  Ordering" ("the top item will be auto-selected first"). Lower = first. */
  gbb_order: number | null;
  /** PB-AI-01: curated homeowner copy — when set, it WINS on the proposal
   *  page; the AI only writes when it's empty (his app's exact behavior). */
  homeowner_overview: string | null;
  /** One feature per line — feeds the proposal's AI copy either way. */
  key_features: string | null;
  /** QUOTE-POLISH-07: this MODEL's standard warranty (SLP99 = 12 / 7). Copied
   *  onto every quote line that picks it; each unit can still be changed. */
  warranty_parts_years: number | null;
  warranty_labor_years: number | null;
}

/** Editable draft — id present = update, absent = insert. */
export type ItemDraft = Partial<Item> & { part_number: string };

/** QUOTE-POLISH-08 (Seth's rules, mirrored from `pegasus_series_warranty` in the
 *  DB): Lennox Signature (SL… / SLP… models) 12/7 · Elite (EL…) 10/5 · Merit (ML…) 5/2 ·
 *  every Armstrong unit 10/5. Null = no rule (the tier standard applies). */
export function seriesWarranty(manufacturer: string | null | undefined, model: string | null | undefined): { parts: number; labor: number } | null {
  const mfg = (manufacturer ?? "").toLowerCase();
  const m = (model ?? "").toUpperCase();
  if (mfg.includes("armstrong")) return { parts: 10, labor: 5 };
  if (mfg.includes("lennox")) {
    if (m.startsWith("SL")) return { parts: 12, labor: 7 };
    if (m.startsWith("EL")) return { parts: 10, labor: 5 };
    if (m.startsWith("ML")) return { parts: 5, labor: 2 };
  }
  return null;
}

export function useItemCategories() {
  return useQuery({
    queryKey: ["item_categories"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      // Paged — the pricebook is past PostgREST's 1000-row cap (the list was cut short).
      const data = await pagedAll<{ category: string | null }>(() =>
        supabase.from("item").select("category").not("category", "is", null).order("id"));
      const set = new Set<string>();
      for (const r of data) {
        if (r.category) set.add(r.category);
      }
      return [...set].sort();
    },
  });
}

/** Distinct values of a text column on `item` — powers the managed comboboxes. */
function useDistinct(column: "subcategory" | "manufacturer", scopeCategory?: string | null) {
  return useQuery({
    queryKey: ["item_distinct", column, scopeCategory ?? null],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      // Paged — the pricebook is past PostgREST's 1000-row cap (the list was cut short).
      const data = await pagedAll<Record<string, string | null>>(() => {
        let q = supabase.from("item").select(column).not(column, "is", null);
        if (column === "subcategory" && scopeCategory) q = q.eq("category", scopeCategory);
        return q.order("id");
      });
      const set = new Set<string>();
      for (const r of data) {
        const v = r[column];
        if (v) set.add(v);
      }
      return [...set].sort((a, b) => a.localeCompare(b));
    },
  });
}
export const useManufacturers = () => useDistinct("manufacturer");
export const useSubcategories = (category?: string | null) => useDistinct("subcategory", category);

export interface Vendor { code: string; name: string | null; web_url: string | null }

/**
 * Vendor list for the picker. Prefers names from `mirror_vendor` (the ESC
 * dbo.Vendor mirror); falls back to the bare codes already on items so the
 * picker works even before the vendor names are synced. Returns a resolver too.
 */
export function useVendors() {
  return useQuery({
    queryKey: ["vendors"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<{ list: Vendor[]; label: (code: string | null) => string }> => {
      const [{ data: mv }, { data: used }] = await Promise.all([
        supabase.from("mirror_vendor").select("vendor_code, name, web_url"),
        supabase.from("item").select("vendor").not("vendor", "is", null),
      ]);
      const byCode = new Map<string, Vendor>();
      for (const v of (mv ?? []) as { vendor_code: string; name: string | null; web_url: string | null }[]) {
        byCode.set(v.vendor_code, { code: v.vendor_code, name: v.name, web_url: v.web_url });
      }
      // ensure every code present on items appears even without a mirror row
      for (const r of (used ?? []) as { vendor: string | null }[]) {
        if (r.vendor && !byCode.has(r.vendor)) byCode.set(r.vendor, { code: r.vendor, name: null, web_url: null });
      }
      const list = [...byCode.values()].sort((a, b) =>
        (a.name ?? a.code).localeCompare(b.name ?? b.code));
      const label = (code: string | null) => {
        if (!code) return "";
        const v = byCode.get(code);
        return v?.name ? v.name : code;
      };
      return { list, label };
    },
  });
}

/**
 * Item purchase history (ITEMS-HIST) — every time we bought this part: date,
 * vendor, qty, unit cost, PO#. From the ESC PO-line mirror (mirror_po_led)
 * joined to the PO header (vendor + date) + vendor name. Lets the office track
 * item cost over time and which vendor gave what pricing. Read-only (DEC-22).
 */
export interface PurchaseEvent {
  po: string; date: string | null;
  vendor_code: string | null; vendor_name: string | null;
  qty: number | null; unit_cost: number | null;
  source?: "esc" | "pegasus";
}

type PoHead = { po: string; date: string | null; vendor_code: string | null };
type PegPurchase = { po_id: string | null; vendor_code: string | null; qty: number | null; unit_cost: number | null; received_date: string | null };

export function useItemPurchaseHistory(part: string | null) {
  return useQuery({
    queryKey: ["item_purchase_history", part],
    enabled: !!part,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<PurchaseEvent[]> => {
      // ESC PO lines + Pegasus-native received purchases (merged history)
      const [{ data: lines }, { data: peg }] = await Promise.all([
        supabase.from("mirror_po_led").select("po, quan, price").eq("part", part!).limit(300),
        supabase.from("pegasus_purchase").select("po_id, vendor_code, qty, unit_cost, received_date").eq("part_number", part!).limit(100),
      ]);
      const escLines = (lines ?? []) as { po: string; quan: number | null; price: number | null }[];
      const pegRows = (peg ?? []) as PegPurchase[];
      const pos = [...new Set(escLines.map((r) => r.po))];
      const headers: PoHead[] = pos.length
        ? (((await supabase.from("mirror_po").select("po, date, vendor_code").in("po", pos)).data ?? []) as PoHead[])
        : [];
      const hMap = new Map(headers.map((h) => [h.po, h]));
      const codes = [...new Set([
        ...headers.map((h) => h.vendor_code),
        ...pegRows.map((p) => p.vendor_code),
      ].filter(Boolean))] as string[];
      let vMap = new Map<string, string | null>();
      if (codes.length > 0) {
        const { data: vs } = await supabase.from("mirror_vendor").select("vendor_code, name").in("vendor_code", codes);
        vMap = new Map((vs ?? []).map((v: { vendor_code: string; name: string | null }) => [v.vendor_code, v.name]));
      }
      const escEvents: PurchaseEvent[] = escLines.map((r) => {
        const h = hMap.get(r.po);
        return { po: r.po, date: h?.date ?? null, vendor_code: h?.vendor_code ?? null,
          vendor_name: h?.vendor_code ? vMap.get(h.vendor_code) ?? null : null, qty: r.quan, unit_cost: r.price, source: "esc" };
      });
      const pegEvents: PurchaseEvent[] = pegRows.map((p) => ({
        po: "Pegasus", date: p.received_date, vendor_code: p.vendor_code,
        vendor_name: p.vendor_code ? vMap.get(p.vendor_code) ?? null : null, qty: p.qty, unit_cost: p.unit_cost, source: "pegasus",
      }));
      return [...pegEvents, ...escEvents].sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
    },
  });
}

export interface VendorRecommendation {
  recommendation: {
    vendor_code: string; vendor_name: string; price: number | null; last_date: string | null;
    buys: number; reason: string; watch_outs: string | null; savings_vs_last: number | null;
  } | null;
  last_purchase: { name: string; price: number | null; date: string | null } | null;
  vendor_count: number;
  note?: string;
}

/** Purchasing AI (PURCHASE-AI) — "where should we order this?". Lazy: only fires
 *  when `enabled` (a button), grounded in real VenParts/PO data server-side. */
export function useVendorRecommendation(part: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["vendor_recommendation", part],
    enabled: enabled && !!part,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<VendorRecommendation> => {
      const { data, error } = await supabase.functions.invoke("recommend-vendor", { body: { part } });
      if (error) throw error;
      return data as VendorRecommendation;
    },
  });
}

export function useItems(
  search: string,
  category: string | null,
  showArchived = false,
  showInactive = false,
  manufacturer: string | null = null,
  equipType: string | null = null,
) {
  return useQuery({
    queryKey: ["items", search.trim().toLowerCase(), category, showArchived, showInactive, manufacturer, equipType],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<Item[]> => {
      // PRICEBOOK-SEARCH-01: a typed search goes through the one pricebook search
      // (every word, sizes, dashes, best first) with the page's filters.
      const typed = search.trim();
      if (typed) {
        return searchPricebook(typed, 500, {
          category, manufacturer, equipType, includeInactive: showInactive, includeArchived: showArchived,
        });
      }
      let q = supabase.from("item").select("*");
      // Archived items are soft-removed — hidden unless explicitly shown.
      if (!showArchived) q = q.eq("archived", false);
      // Inactive items hidden by default too (Seth) — toggle to reveal.
      if (!showInactive) q = q.eq("inactive", false);
      if (category) q = q.eq("category", category);
      // Brand-scope (Lennox/Armstrong) + equipment-type scope for catalog cleanup + GBB pricing.
      if (manufacturer) q = q.ilike("manufacturer", manufacturer);
      if (equipType) q = q.eq("equip_type", equipType);
      const { data, error } = await q.order("part_number").limit(500);
      if (error) throw error;
      return (data ?? []) as Item[];
    },
  });
}

/** Synthetic catalog keys from the proposal-app accessory import (PA-001…057
 *  plus a name suffix, e.g. PA-041-T6). They're database keys, not model
 *  numbers — nothing customer- or office-facing should ever lead with one. */
export function isSyntheticPart(pn: string | null | undefined): boolean {
  return !!pn && /^PA-\d{3}/i.test(pn);
}

/** Equipment / accessories for the GBB quote builder, filtered to one equip_type
 * (Furnace / Air Conditioner / Heat Pump / Accessory). Ordered tier then price. */
export function useEquipmentItems(equipType: string | null, search: string, brand?: string | null, subcategory?: string | null, category?: string | null) {
  return useQuery({
    queryKey: ["equipment_items", equipType, brand ?? "", subcategory ?? "", category ?? "", search.trim().toLowerCase()],
    enabled: !!equipType || !!category,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<Item[]> => {
      // PRICEBOOK-SEARCH-01: a typed search uses the one pricebook search (best first),
      // scoped the same way. QUOTE-POLISH-01: the accessory sub-category is no longer a
      // HARD filter on typed searches — the picker promises "type to search everything",
      // so in-category hits rank FIRST and the rest of the catalog follows (the picker
      // draws the divider). Untyped lists stay strictly scoped.
      const typed = search.trim();
      if (typed) {
        const hits = await searchPricebook(typed, 300, { equipType, category: category ?? null, manufacturer: brand ?? null });
        if (!subcategory) return hits;
        const inScope = hits.filter((h) => h.subcategory === subcategory);
        const outside = hits.filter((h) => h.subcategory !== subcategory);
        return [...inScope, ...outside];
      }
      // Inactive items ("Do not use") never go on a proposal.
      let q = supabase.from("item").select("*").eq("archived", false).not("inactive", "is", true);
      // Equipment slots filter by equip_type; repair-component cards filter by item.category
      // (the failed part — capacitor, motor, inducer… — which carries the all-in flat-rate price).
      if (equipType) q = q.eq("equip_type", equipType);
      if (category) q = q.eq("category", category);
      // Brand-scope equipment to the collection's brand (Lennox ↔ Armstrong).
      // `ilike` with no wildcards = case-insensitive exact match (handles "LENNOX"/"Lennox").
      if (brand) q = q.ilike("manufacturer", brand);
      // Accessory category-scope (Thermostats / Filters / IAQ …) — the proposal-app dropdowns.
      if (subcategory) q = q.eq("subcategory", subcategory);
      const { data, error } = await q
        .order("tier", { nullsFirst: false })
        // GBB-ORDER-01: inside a tier, the office's ranking wins ("the top
        // item will be auto-selected first"); price breaks the remaining ties.
        .order("gbb_order", { nullsFirst: false })
        .order("sell_price", { nullsFirst: false })
        .limit(300);
      if (error) throw error;
      return (data ?? []) as Item[];
    },
  });
}

/** Accessory categories (subcategories) that have at least one active item — powers the
 * proposal-app-style category dropdown cards in the quote builder. */
export function useAccessorySubcategories() {
  return useQuery({
    queryKey: ["accessory_subcategories"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("item")
        .select("subcategory")
        .eq("archived", false)
        .eq("equip_type", "Accessory")
        .not("subcategory", "is", null);
      if (error) throw error;
      const set = new Set<string>();
      for (const r of (data ?? []) as { subcategory: string | null }[]) {
        if (r.subcategory) set.add(r.subcategory);
      }
      return [...set].sort((a, b) => a.localeCompare(b));
    },
  });
}

/** Distinct pricebook equip_types (Furnace / Air Conditioner / Heat Pump / Water Heater /
 * Accessory …) — the options a quote category maps its slot to. */
export function useEquipTypes() {
  return useQuery({
    queryKey: ["equip_types"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("item")
        .select("equip_type")
        .eq("archived", false)
        .not("equip_type", "is", null);
      if (error) throw error;
      const set = new Set<string>();
      for (const r of (data ?? []) as { equip_type: string | null }[]) {
        if (r.equip_type) set.add(r.equip_type);
      }
      return [...set].sort((a, b) => a.localeCompare(b));
    },
  });
}

function invalidateItems(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["items"] });
  qc.invalidateQueries({ queryKey: ["item_categories"] });
}

export function useSaveItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string } & Partial<Item>) => {
      const { id, ...patch } = v;
      const { error } = await supabase
        .from("item")
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => invalidateItems(qc),
  });
}

/** Create (no id) or update (with id) a full item from the editor. Returns the row. */
export function useUpsertItemFull() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (draft: ItemDraft & { id?: string }): Promise<Item> => {
      const { id, ...rest } = draft;
      const payload = { ...rest, updated_at: new Date().toISOString() };
      if (id) {
        const { data, error } = await supabase
          .from("item").update(payload).eq("id", id).select().single();
        if (error) throw error;
        return data as Item;
      }
      const { data, error } = await supabase
        .from("item")
        .insert({ source: "pegasus", ...payload })
        .select().single();
      if (error) throw error;
      return data as Item;
    },
    onSuccess: () => invalidateItems(qc),
  });
}

/** Archive (soft-remove) or restore an item. ESC is never written (DEC-22). */
export function useArchiveItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) => {
      const { error } = await supabase
        .from("item")
        .update({ archived, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => invalidateItems(qc),
  });
}

export function useBulkSetPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, price, field = "base_price" }: { ids: string[]; price: number; field?: "base_price" | "sell_price" }) => {
      const { error } = await supabase
        .from("item")
        .update({ [field]: price, updated_at: new Date().toISOString() })
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => invalidateItems(qc),
  });
}

export function useBulkPriceAdjust() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, pct, field = "base_price" }: { ids: string[]; pct: number; field?: "base_price" | "sell_price" }) => {
      const { error } = await supabase.rpc("bulk_price_adjust", {
        p_ids: ids,
        p_factor: 1 + pct / 100,
        p_field: field,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidateItems(qc),
  });
}

export function useBulkPriceAddDollar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, amount, field = "base_price" }: { ids: string[]; amount: number; field?: "base_price" | "sell_price" }) => {
      const { error } = await supabase.rpc("bulk_price_add", { p_ids: ids, p_amount: amount, p_field: field });
      if (error) throw error;
    },
    onSuccess: () => invalidateItems(qc),
  });
}

export function useBulkSetActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, inactive }: { ids: string[]; inactive: boolean }) => {
      const { error } = await supabase
        .from("item")
        .update({ inactive, updated_at: new Date().toISOString() })
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => invalidateItems(qc),
  });
}

/* ── PB-ENRICH-01: the AI prefill + the keeper's flags ───────────────────────
   One hard law everywhere: AI fills blanks and fixes provable breakage — it
   never overwrites a human. The prefill returns SUGGESTIONS for the editor
   form (nothing is written until the office saves); the keeper's leftovers
   land in pegasus_pricebook_flag for one-tap resolution here. */

export interface AiPrefillFields {
  product_url?: string | null;
  description?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  equip_type?: string | null;
  subcategory?: string | null;
  efficiency?: string | null;
  warranty_months?: number | null;
  notes?: string | null;
  key_features?: string | null;
  homeowner_overview?: string | null;
}
export interface AiPrefillResult {
  ok: boolean;
  found: boolean;
  fields: AiPrefillFields;
  image_url: string | null;
  source_url: string | null;
  sources: Array<{ uri: string; title: string }>;
}

/** Grounded product lookup for the Item editor — suggestions only, the office
 *  reviews and saves. Works from as little as a part number or model. */
export function useAiPrefill() {
  return useMutation({
    mutationFn: async (v: {
      part_number?: string | null;
      manufacturer?: string | null;
      model?: string | null;
      description?: string | null;
      equip_type?: string | null;
      website_url?: string | null;
    }): Promise<AiPrefillResult> => {
      const { data, error } = await supabase.functions.invoke("pricebook-keeper", { body: { mode: "prefill", ...v } });
      if (error) throw error;
      if (!data?.ok) throw new Error(String(data?.error ?? "AI lookup failed"));
      return data as AiPrefillResult;
    },
  });
}

export interface PricebookFlag {
  id: string;
  item_id: string;
  part_number: string | null;
  kind: string; // dead_image | no_image | missing_field
  field: string | null;
  reason: string | null;
  suggestion: { image_url?: string; source_url?: string } | null;
  status: string;
  created_at: string;
}

export function usePricebookFlags() {
  return useQuery({
    queryKey: ["pb_flags"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<PricebookFlag[]> => {
      const { data, error } = await supabase
        .from("pegasus_pricebook_flag")
        .select("*")
        .eq("status", "open")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as PricebookFlag[];
    },
  });
}

/** Apply a flag's suggestion to the item (when it carries one) or dismiss it. */
export function useResolvePricebookFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { flag: PricebookFlag; action: "apply" | "dismiss" }) => {
      const { data: u } = await supabase.auth.getUser();
      if (v.action === "apply" && v.flag.suggestion) {
        const patch: Record<string, unknown> = {};
        if (v.flag.suggestion.image_url) patch.image_url = v.flag.suggestion.image_url;
        if (v.flag.suggestion.source_url && v.flag.kind === "missing_field") patch.website_url = v.flag.suggestion.source_url;
        if (Object.keys(patch).length) {
          const { error } = await supabase.from("item").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", v.flag.item_id);
          if (error) throw error;
        }
      }
      const { error: fe } = await supabase
        .from("pegasus_pricebook_flag")
        .update({ status: v.action === "apply" ? "applied" : "dismissed", resolved_at: new Date().toISOString(), resolved_by: u?.user?.email ?? null })
        .eq("id", v.flag.id);
      if (fe) throw fe;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pb_flags"] });
      invalidateItems(qc);
    },
  });
}

export interface KeeperLogRow {
  id: string;
  part_number: string | null;
  kind: string;
  field: string | null;
  new_value: string | null;
  source_url: string | null;
  wrote_features: boolean;
  wrote_overview: boolean;
  created_at: string;
}

/** What the keeper did in the last ~26 hours — the "what AI changed last night" digest. */
export function useKeeperDigest() {
  return useQuery({
    queryKey: ["pb_keeper_digest"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<KeeperLogRow[]> => {
      const since = new Date(Date.now() - 26 * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from("pegasus_pricebook_ai_log")
        .select("id, part_number, kind, field, new_value, source_url, wrote_features, wrote_overview, created_at")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as KeeperLogRow[];
    },
  });
}

/** Fetch one item by id (the Keeper panel's "open this item" jump). */
export async function fetchItemById(id: string): Promise<Item | null> {
  const { data } = await supabase.from("item").select("*").eq("id", id).maybeSingle();
  return (data as Item) ?? null;
}
